import { useTranslation } from "react-i18next";
import { DisconnectChannelButton } from "../../components/channel/DisconnectChannelButton.js";
import { ChannelDetailSkeleton } from "../../components/channel/ConnectedChannelLayout.js";

export type ChannelDetailPlatform = "whatsapp" | "instagram" | "facebook";

export type ChannelDetailDisconnectAction = {
  label: string;
  busy: boolean;
  testId: string;
  onClick: () => void;
};

export type ChannelDetailHeaderState = {
  title: string;
  disconnect: ChannelDetailDisconnectAction | null;
};

/** Header registration owned by the route and organization that committed it. */
export type RegisteredChannelDetailHeader = ChannelDetailHeaderState & {
  pathname: string;
  organizationId: number;
};

export type ResolvedChannelDetailHeader = {
  pending: boolean;
  title: string;
  disconnect: ChannelDetailDisconnectAction | null;
};

export function channelDetailPlatform(pathname: string): ChannelDetailPlatform | null {
  switch (pathname) {
    case "/channels/whatsapp":
      return "whatsapp";
    case "/channels/instagram":
      return "instagram";
    case "/channels/facebook":
      return "facebook";
    default:
      return null;
  }
}

export function channelDetailFallbackKey(
  platform: ChannelDetailPlatform,
): "channels.platforms.whatsapp" | "channels.platforms.instagram" | "channels.platforms.facebook" {
  switch (platform) {
    case "whatsapp":
      return "channels.platforms.whatsapp";
    case "instagram":
      return "channels.platforms.instagram";
    case "facebook":
      return "channels.platforms.facebook";
    default: {
      const unreachable: never = platform;
      return unreachable;
    }
  }
}

function channelDetailLoadingLabelKey(
  platform: ChannelDetailPlatform,
): "whatsapp.loading" | "channels.home.loading" {
  switch (platform) {
    case "whatsapp":
      return "whatsapp.loading";
    case "instagram":
    case "facebook":
      return "channels.home.loading";
    default: {
      const unreachable: never = platform;
      return unreachable;
    }
  }
}

/**
 * A registration is visible only on the route and organization that committed it.
 * Anything else is the destination's generic label, with no disconnect action.
 */
export function resolveChannelDetailHeader(input: {
  pathname: string;
  organizationId: number;
  registered: RegisteredChannelDetailHeader | null;
  genericTitle: string;
}): ResolvedChannelDetailHeader {
  if (channelDetailPlatform(input.pathname) == null) {
    return { pending: false, title: "", disconnect: null };
  }
  if (
    input.registered != null &&
    input.registered.pathname === input.pathname &&
    input.registered.organizationId === input.organizationId
  ) {
    return {
      pending: false,
      title: input.registered.title,
      disconnect: input.registered.disconnect,
    };
  }
  return { pending: true, title: input.genericTitle, disconnect: null };
}

export function applyChannelDetailHeader(
  resolved: ResolvedChannelDetailHeader,
  pathname: string,
): { detailHeader: ChannelDetailHeaderState | null; pending: boolean } {
  if (resolved.pending || channelDetailPlatform(pathname) == null) {
    return { detailHeader: null, pending: resolved.pending };
  }
  return {
    detailHeader: { title: resolved.title, disconnect: resolved.disconnect },
    pending: false,
  };
}

/** Existing detail skeleton for the route whose connection response has not committed. */
export function ChannelDetailRouteSkeleton({
  platform,
}: {
  platform: ChannelDetailPlatform;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <ChannelDetailSkeleton
      label={t(channelDetailLoadingLabelKey(platform))}
      testId="channel-route-loading"
      showTabs={platform === "whatsapp"}
    />
  );
}

export function ChannelDetailHeaderActions({
  pending,
  disconnect,
}: {
  pending: boolean;
  disconnect: ChannelDetailDisconnectAction | null;
}): React.JSX.Element | null {
  if (pending) {
    return (
      <div
        className="h-8 w-24 animate-pulse rounded-md bg-surface-muted motion-reduce:animate-none"
        data-testid="channels-header-action-skeleton"
        aria-hidden="true"
      />
    );
  }
  if (disconnect == null) return null;
  return (
    <DisconnectChannelButton
      label={disconnect.label}
      busy={disconnect.busy}
      testId={disconnect.testId}
      onClick={disconnect.onClick}
    />
  );
}
